import React, { useState, useEffect } from 'react'; 
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './UserProfile.css';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// Class attendance chart component
const ClassAttendanceChart = ({ bookings }) => {
  const { t } = useTranslation();
  
  const createChartData = () => {
    // Get current year classes that are completed
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // Filter classes from this year that are already completed
    const completedClasses = bookings.filter(booking => {
      const bookingDate = new Date(booking.start_time);
      return bookingDate.getFullYear() === currentYear && 
             bookingDate < now && 
             booking.status.toLowerCase() === 'confirmed';
    });
    
    // Count classes by type
    const classCounts = {};
    completedClasses.forEach(booking => {
      const className = booking.class_name || 'Unknown';
      classCounts[className] = (classCounts[className] || 0) + 1;
    });
    
    // Colors for chart
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', 
      '#FF9F40', '#8AC926', '#FF595E', '#1982C4', '#6A4C93'
    ];
    
    // Generate chart data
    const labels = Object.keys(classCounts);
    const data = Object.values(classCounts);
    const backgroundColor = labels.map((_, i) => colors[i % colors.length]);
    
    return {
      labels,
      datasets: [{
        data,
        backgroundColor,
        borderWidth: 1
      }]
    };
  };
  
  // If no data, show message
  if (!bookings || bookings.length === 0) {
    return <div className="no-chart-data"><p>{t('no_classes_attended_yet')}</p></div>;
  }
  
  const chartData = createChartData();
  
  // If no classes completed this year
  if (chartData.labels.length === 0) {
    return <div className="no-chart-data"><p>{t('no_classes_attended_yet')}</p></div>;
  }
  
  // Chart options
  const options = {
    plugins: {
      legend: {
        position: 'right',
        labels: { 
          boxWidth: 15,
          font: { size: 12 }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    maintainAspectRatio: false
  };
  
  try {
    return (
      <div className="pie-chart-wrapper">
        <Pie data={chartData} options={options} />
      </div>
    );
  } catch (error) {
    console.error("Error rendering chart:", error);
    return <div className="no-chart-data"><p>Error rendering chart</p></div>;
  }
};

const UserProfile = () => {
  const { t } = useTranslation();
  const { currentUser, updateProfile, getUserBookings, logout } = useAuth();
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [completedClasses, setCompletedClasses] = useState(0);
  const [cancellingBookingId, setCancellingBookingId] = useState(null);
  const navigate = useNavigate();

  // Add UTF-8 meta tag to ensure proper encoding
  useEffect(() => {
    // Check if the charset meta tag already exists
    let charsetMeta = document.querySelector('meta[charset]');
    if (!charsetMeta) {
      // Create and append the charset meta tag if it doesn't exist
      charsetMeta = document.createElement('meta');
      charsetMeta.setAttribute('charset', 'utf-8');
      document.head.insertBefore(charsetMeta, document.head.firstChild);
    } else {
      // Ensure it's set to UTF-8
      charsetMeta.setAttribute('charset', 'utf-8');
    }
    
    // Also ensure Content-Type meta is present
    let contentTypeMeta = document.querySelector('meta[http-equiv="Content-Type"]');
    if (!contentTypeMeta) {
      contentTypeMeta = document.createElement('meta');
      contentTypeMeta.setAttribute('http-equiv', 'Content-Type');
      contentTypeMeta.setAttribute('content', 'text/html; charset=UTF-8');
      document.head.appendChild(contentTypeMeta);
    } else {
      contentTypeMeta.setAttribute('content', 'text/html; charset=UTF-8');
    }
  }, []);

  // Initialize form with user data
  useEffect(() => {
    if (currentUser) {
      setProfileData({
        name: currentUser.name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || ''
      });
    }
  }, [currentUser]);

  // Fetch user bookings
  useEffect(() => {
    const fetchBookings = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const bookingsData = await getUserBookings();
        setBookings(bookingsData || []);
        
        // Calculate completed classes for this year
        const now = new Date();
        const currentYear = now.getFullYear();
        
        const completedCount = bookingsData.filter(booking => {
          const bookingDate = new Date(booking.start_time);
          return bookingDate.getFullYear() === currentYear && 
                 bookingDate < now && 
                 booking.status.toLowerCase() === 'confirmed';
        }).length;
        
        setCompletedClasses(completedCount);
        setError('');
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError(t('failed_load_bookings'));
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [currentUser, getUserBookings, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    
    try {
      await updateProfile(profileData);
      setIsEditing(false);
      setSuccessMsg(t('update_profile_success'));
    } catch (err) {
      setError(err.message || t('failed_update_profile'));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(t('date_locale'), {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC' // Treat the date as UTC to fix +2h issue
    });
  };

  // Future?
  const isUpcoming = (dateString) => {
    return new Date(dateString) > new Date();
  };

  // Cancellation
  const handleCancelBooking = async (bookingId) => {
    try {
      setCancellingBookingId(bookingId);
      await axios.post(`/bookings/${bookingId}/cancel`);
      setBookings(prev =>
        prev.map(b =>
          b.booking_id === bookingId ? { ...b, status: 'cancelled' } : b
        )
      );
    } catch (err) {
      console.error('Error cancelling booking:', err);
      alert(t('failed_cancel_booking'));
    } finally {
      setCancellingBookingId(null);
    }
  };

  // Only upcoming & confirmed
  const getFutureBookings = () => {
    return bookings.filter(booking => 
      isUpcoming(booking.start_time) && booking.status.toLowerCase() === 'confirmed'
    );
  };

  if (!currentUser) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>{t('loading_user_profile')}</p>
      </div>
    );
  }

  const futureBookings = getFutureBookings();

  return (
    <div className="profile-container">
      {/* Header */}
      <div className="profile-header">
        <h1>{t('my_profile')}</h1>
        <button className="logout-button" onClick={handleLogout}>
          {t('logout')}
        </button>
      </div>
      
      {/* Messages */}
      {error && <div className="error-message">{error}</div>}
      {successMsg && <div className="success-message">{successMsg}</div>}
      
      {/* Profile Info / Edit */}
      <div className="profile-content">
        <div className="profile-section">
          <h2>{t('personal_information')}</h2>
        
          {isEditing ? (
            <form onSubmit={handleSubmit} className="profile-form">
              <div className="form-group">
                <label htmlFor="name">{t('full_name_label')}</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={profileData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">{t('email_label')}</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={profileData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">{t('phone_label')}</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={profileData.phone || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="save-button">{t('save_changes')}</button>
                <button type="button" className="cancel-button" onClick={() => setIsEditing(false)}>
                  {t('cancel_button')}
                </button>
              </div>
            </form>
          ) : (
            <div className="profile-info">
              <div className="info-row">
                <span className="info-label">{t('name_label')}</span>
                <span className="info-value">{currentUser.name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">{t('email_label')}</span>
                <span className="info-value">{currentUser.email}</span>
              </div>
              <div className="info-row">
                <span className="info-label">{t('phone_label')}</span>
                <span className="info-value">{currentUser.phone || t('not_provided')}</span>
              </div>
              <div className="classes-attended">
                <span className="classes-count">{completedClasses}</span>{' '}
                {t('classes_attended')}
              </div>
              
              {/* Class Type Distribution Chart */}
              {completedClasses > 0 && (
                <div className="chart-section">
                  <h3>{t('class_type_distribution')}</h3>
                  {bookings && bookings.length > 0 && (
                    <ClassAttendanceChart bookings={bookings} />
                  )}
                </div>
              )}
              
              <button className="edit-button" onClick={() => setIsEditing(true)}>
                {t('edit_profile')}
              </button>
            </div>
          )}
        </div>
        
        {/* Upcoming Bookings */}
        <div className="profile-section">
          <h2>{t('upcoming_bookings')}</h2>
          
          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>{t('loading_your_bookings')}</p>
            </div>
          ) : futureBookings.length > 0 ? (
            <div className="bookings-list compact">
              {futureBookings.slice(0, 3).map(booking => {
                const startsAt = new Date(booking.start_time);
                const now = new Date();
                const hoursUntil = (startsAt - now) / (1000 * 60 * 60);
                const tooLate = hoursUntil < 12;

                return (
                  <div key={booking.booking_id} className="booking-card compact upcoming">
                    <div className="booking-header">
                      <h3>{booking.class_name}</h3>
                      <span className="booking-status status-confirmed">✅ {t('confirmed')}</span>
                    </div>
                    
                    <div className="booking-details">
                      <div className="booking-details-row">
                        <p>
                          <strong>{t('date_time_label')}</strong>
                          <span className="detail-value">{formatDate(booking.start_time)}</span>
                        </p>
                        <p>
                          <strong>{t('location_label')}</strong>
                          <span className="detail-value">{booking.location}</span>
                        </p>
                      </div>
                      <div className="booking-details-row">
                        <p>
                          <strong>{t('instructor_label')}</strong>
                          <span className="detail-value">{booking.instructor_name}</span>
                        </p>
                        <p>
                          <strong>{t('duration_label')}</strong>
                          <span className="detail-value">{booking.duration} {t('min')}</span>
                        </p>
                      </div>
                    </div>
                    
                    <div className="booking-actions">
                      <button
                        className="cancel-booking-btn"
                        disabled={cancellingBookingId === booking.booking_id || tooLate}
                        onClick={() => {
                          if (tooLate) {
                            alert(t('too_late_to_cancel'));
                          } else {
                            handleCancelBooking(booking.booking_id);
                          }
                        }}
                      >
                        {tooLate
                          ? t('cancellation_disabled')
                          : cancellingBookingId === booking.booking_id
                            ? t('cancelling_booking')
                            : t('cancel_booking')}
                      </button>
                    </div>
                  </div>
                );
              })}
              
              <div className="view-all-container">
                <button className="view-all-btn" onClick={() => navigate('/bookings', { state: { scrollToTop: true } })}>
                  {t('view_all_bookings')}
                </button>
              </div>
            </div>
          ) : (
            <div className="no-bookings">
              <div className="no-data-icon">🧘‍♀️ </div>
              <p>{t('dont_have_upcoming_bookings')}</p>
              <p>{t('check_out_schedule')}</p>
              <button className="view-schedule-btn" onClick={() => navigate('/schedule')}>
                {t('view_schedule')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
